/**
 * Shared overlay plumbing. `window.ThemeOverlay` owns three things that
 * every overlay in the theme needs and each used to reimplement:
 *
 *   1. open/close MOTION for the four top-layer <dialog> overlays - the
 *      cart drawer (assets/cart.js), quick view (assets/quick-view.js), the
 *      concierge modal (assets/perfect-match.js) and the product gallery
 *      lightbox (assets/gallery.js) - plus focus restore for all six.
 *   2. the FOCUS TRAP primitives (getFocusable/trapTab), also used by the
 *      two <details>-based overlays in assets/header.js, which are not
 *      dialogs and get no native trap.
 *   3. the SCROLL LOCK, which is stack-aware because these overlays stack.
 *
 * (2) and (3) are further down; the motion notes below come first because
 * they explain why (1) has to be JS at all.
 *
 * Why this is JS and not pure CSS
 * -------------------------------
 * The obvious CSS-only shape - @starting-style plus
 * `transition: display/overlay allow-discrete` - does not work on these
 * elements. It was measured directly on .cart-drawer[open]: a synchronous
 * getComputedStyle a few milliseconds after showModal() already reports the
 * RESTING open transform, and no transitionstart ever fires, so the
 * starting-style value is never applied and the panel reads as instant.
 * (The same pattern does work on a plain <dialog> elsewhere, so this is
 * something about these elements rather than about the pattern.)
 *
 * So the "from" state is committed as a real, painted style instead:
 *   open()  showModal() paints the closed rest state, then .is-visible is
 *           added two frames later, giving the transition a real box to
 *           interpolate away from. Two frames, not one: a single rAF can
 *           still land before the first paint that reflects showModal().
 *   close() removes .is-visible and waits for the transition to finish
 *           before calling dialog.close(), so the panel leaves the way it
 *           arrived instead of snapping out on the same frame.
 *
 * Every consumer's CSS therefore follows the same shape:
 *   .thing               { <transition>; }
 *   .thing[open]:not(.is-visible) { <the closed "from" state> }
 *   .thing[open].is-visible::backdrop { opacity: 1 }
 * all inside @media (prefers-reduced-motion: no-preference), with
 * closeDialog() below short-circuiting to an instant close when the user
 * has asked for reduced motion.
 *
 * Loaded early in layout/theme.liquid so it is defined before any consumer
 * runs, but every call site still guards on window.ThemeOverlay and falls
 * back to a plain showModal()/close() if it is missing.
 */
(function () {
  if (window.ThemeOverlay) return;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * showModal() the dialog and let it animate in.
   */
  function openDialog(dialog) {
    if (!dialog) return;
    if (dialog.open) {
      /* Reopened while its close animation is still running (clicking the
         product image again right after closing the zoom). The dialog is
         technically still open, so this used to return here - and the
         pending close then finished, swallowing the click. Call the close
         off and fade back in instead. */
      if (dialog.__overlayClose) {
        cancelClose(dialog);
        dialog.classList.add('is-visible');
      }
      return;
    }
    dialog.showModal();
    if (prefersReducedMotion()) {
      dialog.classList.add('is-visible');
      return;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (dialog.open) dialog.classList.add('is-visible');
      });
    });
  }

  /**
   * Reverse the entry animation, then close(). Safe to call repeatedly -
   * a second call while a close is already in flight is a no-op, and the
   * guard timer/listener are always torn down exactly once.
   */
  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;

    var pending = dialog.__overlayClose;
    if (pending) return;

    if (prefersReducedMotion() || !dialog.classList.contains('is-visible')) {
      dialog.classList.remove('is-visible');
      dialog.close();
      return;
    }

    var finished = false;
    var onTransitionEnd = function (event) {
      // Only the dialog's own transition counts - children (the panel's
      // own hover effects, a spinner) bubble transitionend up through it.
      if (event.target !== dialog) return;
      finish();
    };
    var finish = function () {
      if (finished) return;
      finished = true;
      dialog.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(dialog.__overlayCloseTimer);
      dialog.__overlayClose = null;
      dialog.__overlayCloseCancel = null;
      if (dialog.open) dialog.close();
    };

    dialog.__overlayClose = finish;
    /* Lets cancelClose() call the whole thing off, listener included - a
       cleared timer alone left the transitionend listener live, and the
       next transition on the dialog would close it. */
    dialog.__overlayCloseCancel = function () {
      finished = true;
      dialog.removeEventListener('transitionend', onTransitionEnd);
    };
    dialog.addEventListener('transitionend', onTransitionEnd);
    // Belt-and-suspenders for the case where transitionend never fires
    // (e.g. the panel is display:none-d out from under the transition some
    // other way): --motion-overlay is 320ms, plus a small buffer.
    dialog.__overlayCloseTimer = setTimeout(finish, 400);

    dialog.classList.remove('is-visible');
  }

  /**
   * Tear-down hook for a consumer's disconnectedCallback: drops any
   * in-flight close so a removed element can't fire a timer later.
   */
  /** True while closeDialog()'s exit animation is still running. */
  function isClosing(dialog) {
    return Boolean(dialog && dialog.__overlayClose);
  }

  function cancelClose(dialog) {
    if (!dialog) return;
    clearTimeout(dialog.__overlayCloseTimer);
    if (dialog.__overlayCloseCancel) dialog.__overlayCloseCancel();
    dialog.__overlayClose = null;
    dialog.__overlayCloseCancel = null;
  }

  /**
   * Focus restore, shared by every overlay: return the caret to whatever
   * opened the overlay, but only if that element is still in the document
   * (an add-to-cart submit button, for instance, is routinely replaced by
   * a Section Rendering swap while the drawer is open - focusing a
   * detached node silently sends focus to <body> instead).
   */
  function restoreFocus(triggerEl) {
    if (!triggerEl || typeof triggerEl.focus !== 'function') return;
    if (!document.contains(triggerEl)) return;
    triggerEl.focus();
  }

  /* ---------------------------------------------------------------- */
  /* Focus trap                                                        */
  /* ---------------------------------------------------------------- */
  /**
   * This selector string used to exist verbatim in three files
   * (quick-view.js, perfect-match.js, header.js) and the wrap-around block
   * below in four places (those three plus header.js's <search-overlay>).
   * They had not drifted yet, which is exactly why they were worth
   * collapsing before they did.
   *
   * `[tabindex]:not([tabindex="-1"])` deliberately keeps positive
   * tabindexes in the list; nothing in this theme sets one, and excluding
   * them would silently drop an element a future template might add.
   */
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * Every tabbable descendant of `root`, in DOM order, minus the ones that
   * are not rendered.
   *
   * The offsetParent filter is the careful bit and all four originals had
   * it: an overlay routinely contains focusable elements the visitor cannot
   * see - the cart drawer's inactive tab panel is `hidden`, the nav
   * expansion's preview sets are `hidden`, the quick-view content is empty
   * while it loads. Without the filter, Tab lands on nothing.
   *
   * offsetParent is null for display:none subtrees (what `hidden` produces)
   * and also for position:fixed elements. The second case is why this is
   * only ever called with a CONTENT root (a dialog's contents, a panel) and
   * never with a fixed panel itself as the sole candidate.
   */
  function getFocusable(root) {
    if (!root) return [];
    return Array.prototype.slice
      .call(root.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        return el.offsetParent !== null;
      });
  }

  /**
   * The wrap-around half of a focus trap, for a Tab keydown. Serves both
   * the <dialog> overlays (belt-and-braces over the native top-layer trap)
   * and the two <details> disclosures in header.js, which have no native
   * trap at all and genuinely need this.
   *
   * Only the two edges are handled: Tab on the last element wraps to the
   * first, Shift+Tab on the first wraps to the last. Every move in between
   * is left to the browser, which is better at it - notably inside a
   * <select>, or a radio group where arrow keys and Tab disagree about what
   * "next" means.
   */
  function trapTab(event, root) {
    if (!event || event.key !== 'Tab') return;
    var focusable = getFocusable(root);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------- */
  /* Scroll lock                                                       */
  /* ---------------------------------------------------------------- */
  /**
   * One class (`html.overlay-open`, assets/base.css) replaces the four
   * identical ones each overlay used to own - .cart-drawer-open,
   * .quick-view-open, .perfect-match-open, .search-overlay-lock.
   *
   * Overlays in this theme really do stack: quick view opens ON TOP of an
   * already-open cart drawer (that is its only trigger - the drawer's
   * "Recently viewed" tab), and closing quick view after an add-to-cart
   * leaves the drawer open behind it. A single boolean would unlock the
   * page the moment quick view closed, with the drawer still up.
   *
   * So this tracks WHICH overlays hold a lock rather than how many. A list
   * of owners, not a counter: a counter is only correct if every lock is
   * paired with exactly one unlock, and <search-overlay> calls its lock
   * from a `toggle` handler that can fire more than once for one open.
   * Re-locking the same owner is a no-op here, and unlocking an owner that
   * never locked cannot drive the count negative or, worse, unlock early.
   */
  var scrollLockOwners = [];

  function lockScroll(owner) {
    var key = owner || document;
    if (scrollLockOwners.indexOf(key) === -1) scrollLockOwners.push(key);
    document.documentElement.classList.add('overlay-open');
  }

  function unlockScroll(owner) {
    var key = owner || document;
    var index = scrollLockOwners.indexOf(key);
    if (index !== -1) scrollLockOwners.splice(index, 1);
    if (!scrollLockOwners.length) document.documentElement.classList.remove('overlay-open');
  }

  window.ThemeOverlay = {
    openDialog: openDialog,
    closeDialog: closeDialog,
    cancelClose: cancelClose,
    isClosing: isClosing,
    restoreFocus: restoreFocus,
    prefersReducedMotion: prefersReducedMotion,
    FOCUSABLE_SELECTOR: FOCUSABLE_SELECTOR,
    getFocusable: getFocusable,
    trapTab: trapTab,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
  };
})();
